/**
 * R5 `turns[].attachments[]` chips — each opens the existing app-wide artifact overlay
 * (`GET /api/artifacts/{id}`, `ArtifactCard`'s own fetch), the SAME overlay every other artifact
 * surface in this app opens (`multiAgentArtifactOverlayStore`'s own doc).
 */
import { FileAudio, FileText, Image as ImageIcon, Link2 } from "lucide-react";
import { useArtifactOverlayStore } from "@/stores/multiAgentArtifactOverlayStore";
import type { ConversationAttachment } from "@/services/multi-agent/laneV2/reduce";

const TURN_ATTACHMENT_ICON: Record<string, typeof FileText> = {
  image: ImageIcon,
  pdf: FileText,
  audio: FileAudio,
  url: Link2,
  text: FileText,
};

const CHIP_CLASS = "inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-1.5 py-0.5 text-[11px] text-muted-foreground";

/**
 * Under the reader's own question, right-aligned with it. A chip opens once the server has stored the file (R5 gives
 * its id); until then — the turn is still running — it shows what was sent, without an action.
 */
export function TurnAttachmentChips({ attachments }: { attachments: readonly ConversationAttachment[] }) {
  if (!attachments.length) return null;
  return (
    <div className="flex flex-wrap justify-end gap-1.5" data-lane-v2-turn-attachments="">
      {attachments.map((a, i) => {
        const Icon = TURN_ATTACHMENT_ICON[a.kind] ?? FileText;
        const id = a.attachment_id;
        const open = id
          ? () =>
              useArtifactOverlayStore.getState().open({
                id,
                type: a.kind,
                name: a.name,
                toolName: "intake",
                byteSize: 0,
                createdAt: Date.now(),
                ...(a.previewUrl ? { previewUrl: a.previewUrl } : {}),
              })
          : undefined;
        // An image this browser still holds: the picture itself, the way the composer showed it.
        if (a.previewUrl) {
          const thumb = (
            <img src={a.previewUrl} alt={a.name} className="h-16 w-16 rounded-md border border-border object-cover" />
          );
          return open ? (
            <button
              key={id}
              type="button"
              onClick={open}
              title={`Open ${a.name}`}
              aria-label={`Open ${a.name}`}
              className="rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              data-lane-v2-attachment-thumb=""
            >
              {thumb}
            </button>
          ) : (
            <span key={`sent:${i}:${a.name}`} title={a.name} data-lane-v2-attachment-thumb="">
              {thumb}
            </span>
          );
        }
        const label = (
          <>
            <Icon className="h-3 w-3 shrink-0" aria-hidden />
            <span className="max-w-[160px] truncate">{a.name}</span>
          </>
        );
        return open ? (
          <button
            key={id}
            type="button"
            onClick={open}
            title={`Open ${a.name}`}
            className={`${CHIP_CLASS} hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`}
          >
            {label}
          </button>
        ) : (
          <span key={`sent:${i}:${a.name}`} className={CHIP_CLASS} title={a.name}>
            {label}
          </span>
        );
      })}
    </div>
  );
}
